import * as React from "react";

import { LabeledControl } from "@replohq/lattice/blocks/labeled-control/LabeledControl";
import { Button } from "@replohq/lattice/components/button/Button";
import { Input } from "@replohq/lattice/components/input/Input";
import {
  authButtonClassName,
  authFieldClassName,
} from "@v2/features/account/authFieldStyles";
import {
  ErrorMessage as GlobalErrorMessage,
  ScreenData,
} from "@v2/features/account/common";
import { userRouter } from "@v2/trpc/routes/user";
import { storeToken } from "@v2/utils/store-token";
import { useForm, useWatch } from "react-hook-form";
import { useLocation, useSearchParams } from "react-router-dom";
import { isEmpty } from "replo-utils/lib/misc";
import { isOsmanthusEnabled } from "replo-utils/lib/osmanthus";

type FormValues = {
  password: string;
  userId: string;
  token: string;
};

const ResetPassord: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { mutate: resetPasswordApi, isPending: isLoadingResetPass } =
    userRouter.resetPassword.useMutation({
      onSuccess: ({ token }) => {
        storeToken(token);
        window.location.replace("/");
      },
      onError: () => {
        return setResetPasswordErrorMessage(
          "An unknown error has occurred. Please reach out to support@replo.app for help.",
        );
      },
    });
  const [resetPasswordErrorMessage, setResetPasswordErrorMessage] =
    React.useState<string | null>("");
  const location = useLocation();

  const renderData = ScreenData[location.pathname ?? ""];
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    clearErrors,
  } = useForm<FormValues>({
    defaultValues: {
      password: "",
      /* For super-duper security, each link also contains the userId */
      userId: searchParams.get("userId") ?? "",
      token: searchParams.get("token") ?? "",
    },
    mode: "onBlur",
  });

  const onSubmit = async ({ userId, password, token }: FormValues) => {
    resetPasswordApi({
      userId,
      password,
      token,
    });
  };

  const watchAllFields = useWatch({ control });
  const passwordError = !isEmpty(watchAllFields.password)
    ? errors?.password?.message
    : undefined;

  return (
    <form
      onSubmit={(data) => {
        void handleSubmit(onSubmit)(data);
      }}
    >
      <GlobalErrorMessage errorMessage={resetPasswordErrorMessage} />
      <div className="flex flex-col gap-y-10">
        <LabeledControl label="Password" id="password" error={passwordError}>
          <Input
            UNSAFE_className={
              isOsmanthusEnabled() ? authFieldClassName : undefined
            }
            id="password"
            invalid={Boolean(passwordError)}
            autoComplete="off"
            placeholder="••••••••••••"
            {...register("password", {
              required: "Please enter a valid Password.",
              minLength: {
                value: 8,
                message: "Password should be at least 8 characters long.",
              },
              // NOTE (Fran 2024-02-27): We should clear the errors when the user is typing
              onChange: () => clearErrors("password"),
            })}
            type="password"
            size="base"
          />
        </LabeledControl>
        <Button
          className="w-full"
          UNSAFE_className={
            isOsmanthusEnabled() ? authButtonClassName : undefined
          }
          variant="primary"
          size="base"
          type="submit"
          loading={isLoadingResetPass}
        >
          {renderData?.header}
        </Button>
      </div>
    </form>
  );
};

export default ResetPassord;
